import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardHeader,
  IconButton,
  Avatar,
  TextField,
  Button,
  Typography,
  useTheme,
  styled,
  Fade,
  Skeleton,
  Stack,
  InputAdornment,
  Divider,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import api from "../../../../../utils/apiConfig";
import { format } from "date-fns";

// Styled components
const WizardContainer = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius * 2,
}));

const MessageList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(2),
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.3)",
    },
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isBot",
})(({ theme, isBot }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  flexDirection: isBot ? "row" : "row-reverse",
}));

const BubbleContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isBot",
})(({ theme, isBot }) => ({
  maxWidth: "80%",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  marginLeft: isBot ? theme.spacing(1) : 0,
  marginRight: isBot ? 0 : theme.spacing(1),
  backgroundColor: isBot ? theme.palette.primary.main : theme.palette.grey[100],
  color: isBot
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  boxShadow: theme.shadows[1],
}));

const InputBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: "auto",
}));

const QuickReplyButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export default function ModernGoalWizard({
  householdId,
  userId,
  leftColumnRef,
}) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const messageListRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [currentStep, setCurrentStep] = useState("category");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [chatHeight, setChatHeight] = useState(0);

  const getQuickReplies = useMemo(() => {
    switch (currentStep) {
      case "category":
        return [
          "Income Change",
          "Expense Change",
          "Asset Target",
          "Debt Reduction",
        ];
      case "frequency":
        return ["One-time", "Monthly", "Quarterly", "Annual"];
      case "priority":
        return ["1", "2", "3", "4", "5"];
      case "confirm":
        return ["Yes, create it", "No, start over"];
      default:
        return [];
    }
  }, [currentStep]);

  // Mutation for saving goals
  const addGoal = useMutation({
    mutationFn: (newGoal) =>
      api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/goals-legacy`,
        newGoal,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals", householdId, userId] });
      addMessage(
        "Goal created successfully! Would you like to create another goal?",
        true,
      );
      setCurrentStep("category");
      setFormData({});
    },
    onError: (error) => {
      addMessage(
        `Error creating goal: ${error.message}. Please try again.`,
        true,
      );
    },
  });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize wizard
  useEffect(() => {
    const welcomeMessage =
      "Hi! Let's create your next financial goal. First, what type of goal is this?";
    setMessages([{ text: welcomeMessage, isBot: true, timestamp: new Date() }]);
  }, []);

  // Measure left column height and update chat height
  useEffect(() => {
    const measure = () => {
      if (leftColumnRef.current) {
        const h = leftColumnRef.current.getBoundingClientRect().height;
        setChatHeight(h);
      }
    };

    measure();
    window.addEventListener("resize", measure);

    // Observe leftColumn children if they reflow
    const obs = new MutationObserver(measure);
    if (leftColumnRef.current) {
      obs.observe(leftColumnRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      window.removeEventListener("resize", measure);
      obs.disconnect();
    };
  }, [leftColumnRef]);

  const addMessage = (text, isBot = false) => {
    setMessages((prev) => [...prev, { text, isBot, timestamp: new Date() }]);
  };

  const handleRestart = () => {
    setMessages([
      {
        text: "Hi! Let's create your next financial goal. First, what type of goal is this?",
        isBot: true,
        timestamp: new Date(),
      },
    ]);
    setCurrentStep("category");
    setFormData({});
    setCurrentInput("");
  };

  const handleSend = (e) => {
    e?.preventDefault();
    if (!currentInput.trim()) return;

    addMessage(currentInput, false);
    processUserInput(currentInput);
    setCurrentInput("");
  };

  const handleQuickReply = (value) => {
    addMessage(value, false);
    processUserInput(value);
  };

  const processUserInput = (input) => {
    setIsLoading(true);

    // Simulate processing delay
    setTimeout(() => {
      let nextStep = "";
      let botResponse = "";
      const updatedFormData = { ...formData };

      switch (currentStep) {
        case "category":
          updatedFormData.category = input;
          nextStep = "name";
          botResponse = "Great! What would you like to name this goal?";
          break;
        case "name":
          updatedFormData.name = input;
          nextStep = "amount";
          botResponse =
            "How much money do you need for this goal (in dollars)?";
          break;
        case "amount":
          if (isNaN(input)) {
            botResponse = "Please enter a valid number for the amount.";
            nextStep = "amount";
          } else {
            updatedFormData.amount = parseFloat(input);
            nextStep = "frequency";
            botResponse = "How often does this occur?";
          }
          break;
        case "frequency":
          updatedFormData.frequency = input.toLowerCase();
          nextStep = "start_date";
          botResponse = "When should we start? (YYYY-MM-DD)";
          break;
        case "start_date":
          if (!isValidDate(input)) {
            botResponse = "Please enter a valid date in YYYY-MM-DD format.";
            nextStep = "start_date";
          } else {
            updatedFormData.start_date = input;
            nextStep = "target_date";
            botResponse = "What is your target completion date? (YYYY-MM-DD)";
          }
          break;
        case "target_date":
          if (!isValidDate(input)) {
            botResponse = "Please enter a valid date in YYYY-MM-DD format.";
            nextStep = "target_date";
          } else {
            updatedFormData.target_date = input;
            nextStep = "priority";
            botResponse =
              "On a scale of 1 (low) to 5 (high), how important is this goal?";
          }
          break;
        case "priority":
          const priority = parseInt(input);
          if (priority < 1 || priority > 5) {
            botResponse = "Please enter a priority between 1 and 5.";
            nextStep = "priority";
          } else {
            updatedFormData.priority = priority;
            nextStep = "notes";
            botResponse = "Would you like to add any notes about this goal?";
          }
          break;
        case "notes":
          updatedFormData.metadata = { description: input };
          nextStep = "confirm";
          botResponse =
            "Great! Here's a summary of your goal. Should I create it?";
          break;
        case "confirm":
          if (input.toLowerCase().includes("yes")) {
            const goalData = {
              ...updatedFormData,
              household_id: householdId,
              user_id: userId,
              status: "active",
            };
            addGoal.mutate(goalData);
            nextStep = "category";
          } else {
            botResponse =
              "Okay, let's start over. What type of goal would you like to create?";
            nextStep = "category";
            updatedFormData = {};
          }
          break;
      }

      setFormData(updatedFormData);
      setCurrentStep(nextStep);
      if (botResponse) {
        addMessage(botResponse, true);
      }
      setIsLoading(false);
    }, 500);
  };

  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  // Separate component for quick replies
  const QuickReplies = () => {
    if (!getQuickReplies.length) return null;

    return (
      <Box sx={{ pl: 5, pr: 2, pt: 2 }}>
        {getQuickReplies.map((reply, idx) => (
          <QuickReplyButton
            key={idx}
            variant="outlined"
            size="small"
            onClick={() => handleQuickReply(reply)}
          >
            {reply}
          </QuickReplyButton>
        ))}
      </Box>
    );
  };

  return (
    <WizardContainer
      elevation={2}
      sx={{ height: chatHeight > 0 ? `${chatHeight}px` : "auto" }}
    >
      <CardHeader
        title="Goal Creation Wizard"
        subheader="Let's create a well-structured financial goal"
        action={
          <IconButton
            onClick={handleRestart}
            aria-label="Restart wizard"
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        }
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiCardHeader-title": {
            fontSize: "1.25rem",
            fontWeight: 600,
          },
          height: "73px",
          p: 2,
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <MessageList ref={messageListRef}>
          {messages.map((message, index) => (
            <React.Fragment key={index}>
              <Fade in>
                <MessageBubble isBot={message.isBot}>
                  <Avatar
                    sx={{
                      bgcolor: message.isBot ? "primary.main" : "grey.300",
                      width: 32,
                      height: 32,
                    }}
                  >
                    {message.isBot ? <SmartToyIcon /> : <PersonIcon />}
                  </Avatar>
                  <Stack spacing={0.5}>
                    <BubbleContent isBot={message.isBot}>
                      <Typography variant="body1">{message.text}</Typography>
                    </BubbleContent>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        ml: message.isBot ? 1 : 0,
                        mr: message.isBot ? 0 : 1,
                      }}
                    >
                      {format(message.timestamp, "h:mm a")}
                    </Typography>
                  </Stack>
                </MessageBubble>
              </Fade>
              {/* Show quick replies after the first bot message or the last bot message */}
              {message.isBot &&
                (index === 0 || index === messages.length - 1) && (
                  <QuickReplies />
                )}
            </React.Fragment>
          ))}
          {isLoading && (
            <MessageBubble isBot>
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 32,
                  height: 32,
                }}
              >
                <SmartToyIcon />
              </Avatar>
              <BubbleContent isBot>
                <Skeleton variant="text" width={200} />
              </BubbleContent>
            </MessageBubble>
          )}
        </MessageList>

        <InputBar component="form" onSubmit={handleSend}>
          <TextField
            fullWidth
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            placeholder="Type your message..."
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!currentInput.trim()}
                    color="primary"
                    aria-label="Send message"
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </InputBar>
      </Box>
    </WizardContainer>
  );
}
